import { useEffect, useMemo, useState } from 'react';
import SearchableSelect from '../SearchableSelect';
import './ZZPShared.css';
import './IdVaststelling.css';

function authFetch(url, options = {}) {
  const token = localStorage.getItem('ozel_token');
  return fetch(url, {
    ...options,
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    credentials: 'include',
  });
}

const SOORTEN = ['Nederlandse PP', 'Nederlandse ID-kaart', 'Nederlands rijbewijs', 'Buitenlands paspoort', 'Buitenlandse ID-kaart', 'Verblijfsdocument'];
const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { workerId: '', opdrachtgeverId: '', soortId: SOORTEN[0], documentnummer: '', geldigheidsdatum: '', datumVaststelling: today(), geboortedatum: '' };

export default function IdVaststelling() {
  const [workers, setWorkers] = useState([]);
  const [ogs, setOgs] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');

  useEffect(() => {
    authFetch('/api/workers').then((r) => r.json()).then(setWorkers).catch(() => {});
    authFetch('/api/zzp/opdrachtgevers').then((r) => r.json()).then((d) => setOgs(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  const workerOptions = useMemo(() => workers.map((w) => ({
    value: String(w.id),
    label: `${w.name}${w.bedrijfsnaam ? ` — ${w.bedrijfsnaam}` : ''}`,
  })), [workers]);

  const worker = workers.find((w) => String(w.id) === String(form.workerId));
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setError(''); setDone(''); };

  const pickWorker = (val) => {
    const w = workers.find((x) => String(x.id) === String(val));
    const dob = w?.dateOfBirth ? String(w.dateOfBirth).slice(0, 10) : '';
    setForm((f) => ({
      ...f,
      workerId: val,
      opdrachtgeverId: w?.opdrachtgeverId ? String(w.opdrachtgeverId) : f.opdrachtgeverId,
      geboortedatum: /^\d{4}-\d{2}-\d{2}$/.test(dob) ? dob : '',
    }));
    setError(''); setDone('');
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(''); setDone('');
    try {
      const r = await authFetch('/api/zzp/id-vaststelling', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.error || 'PDF maken mislukt.');
      }
      const blob = await r.blob();
      const cd = r.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename\*=UTF-8''([^;]+)/);
      const name = m ? decodeURIComponent(m[1]) : 'ID Vaststelling.pdf';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      setDone(`${name} is gedownload en staat ook bij Documenten.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const og = ogs.find((o) => String(o.id) === String(form.opdrachtgeverId));

  return (
    <div className="zzp-page idv">
      <div className="zzp-header">
        <div>
          <h2>ID Vaststelling</h2>
          <p>Kies medewerker en bedrijf, vul de ID-gegevens in en download de PDF.</p>
        </div>
      </div>

      <form className="idv-card" onSubmit={submit}>
        <div className="idv-grid">
          <div className="idv-field idv-span2">
            <span>Medewerker</span>
            <SearchableSelect options={workerOptions} value={String(form.workerId)} onChange={pickWorker}
              placeholder="Kies medewerker…" searchPlaceholder="Zoek op naam…" />
          </div>

          <label className="idv-field idv-span2">
            <span>Bedrijf (opdrachtgever)</span>
            <select value={form.opdrachtgeverId} onChange={(e) => set('opdrachtgeverId', e.target.value)}>
              <option value="">— Kies bedrijf —</option>
              {ogs.map((o) => <option key={o.id} value={o.id}>{o.naam}</option>)}
            </select>
          </label>

          {worker && (
            <div className="idv-parties idv-span2">
              <div><small>Opdrachtgever</small>{og ? `${og.naam}, ${og.plaats}` : '—'}</div>
              <div><small>Opdrachtnemer</small>{worker.bedrijfsnaam || <em>bedrijfsnaam ontbreekt</em>}{worker.city ? `, ${worker.city}` : ''}</div>
            </div>
          )}

          <label className="idv-field">
            <span>Soort ID</span>
            <select value={form.soortId} onChange={(e) => set('soortId', e.target.value)}>
              {SOORTEN.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="idv-field">
            <span>Documentnummer</span>
            <input value={form.documentnummer} onChange={(e) => set('documentnummer', e.target.value)} placeholder="bijv. NU29C94L2" />
          </label>
          <label className="idv-field">
            <span>Geldigheidsdatum</span>
            <input type="date" value={form.geldigheidsdatum} onChange={(e) => set('geldigheidsdatum', e.target.value)} />
          </label>
          <label className="idv-field">
            <span>Datum vaststelling</span>
            <input type="date" value={form.datumVaststelling} onChange={(e) => set('datumVaststelling', e.target.value)} />
          </label>
          <label className="idv-field">
            <span>Geboortedatum</span>
            <input type="date" value={form.geboortedatum} onChange={(e) => set('geboortedatum', e.target.value)} />
          </label>
        </div>

        {error && <p className="idv-error" role="alert">{error}</p>}
        {done && <p className="idv-done">{done}</p>}

        <div className="idv-actions">
          <button type="button" className="idv-reset" onClick={() => { setForm({ ...EMPTY, datumVaststelling: today() }); setError(''); setDone(''); }}>
            Leegmaken
          </button>
          <button type="submit" className="zzp-primary-btn" disabled={busy || !form.workerId}>
            {busy ? 'PDF maken…' : 'PDF downloaden'}
          </button>
        </div>
      </form>
    </div>
  );
}
